import type { ValueType } from '@/shared/components/query-builder'
import type { FieldType } from '@/shared/components/query-builder/types'
import type {
	AnchorStrategySettingsGroupModel,
	FilterRuleModel,
} from '@/shared/generated'

import { useState } from 'react'
import { ChevronsUpDownIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	QueryBuilder,
	QueryBuilderRulesList,
} from '@/shared/components/query-builder'
import {
	getDefaultEditorByValueType,
	getFieldIcon,
} from '@/shared/components/query-builder/helpers'
import { localization } from '@/shared/components/query-builder/localization'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Content, Layout } from '@/shared/components/ui/layout'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useAppStore } from '@/shared/contexts/app'

import { useFamilyRulesFieldConfig } from '../../lib/useFamilyRulesFieldConfig'

export default function ApprovalTableFamilyRulesDropdown({
	settings,
}: {
	settings: AnchorStrategySettingsGroupModel
}) {
	const { t } = useTranslation()

	const [open, setOpen] = useState(false)
	const { filters, aggregation } = settings
	const { isLoading } = useFamilyRulesFieldConfig()

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant='ghost' size='sm' className='text-link text-xs'>
					{t('anchoring_management.approval_page.table.view_rules')}
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' side='bottom' className='w-auto p-0'>
				<Layout className='w-225 space-y-4 p-4'>
					<Content className='grid gap-1.5 truncate'>
						{isLoading ? (
							<LoadingPlaceholder />
						) : (
							<ErrorBoundary
								fallback={() => (
									<Alert variant='destructive'>
										<AlertDescription>
											🐞{' '}
											{t('ui_fragment_error', {
												defaultValue:
													'Oops ... it looks like an unexpected error has occurred. Our team`s been notified.',
											})}
										</AlertDescription>
									</Alert>
								)}
							>
								<div className='space-y-4'>
									<FilterRules filters={filters} />
									<Separator />
									<AggregationRules aggregation={aggregation} />
								</div>
							</ErrorBoundary>
						)}
					</Content>
				</Layout>
			</PopoverContent>
		</Popover>
	)
}

const LoadingPlaceholder = () => (
	<div data-testid='loader' className='flex flex-col space-y-2'>
		<Skeleton className='h-10 w-full' />
		<Skeleton className='h-10 w-full' />
	</div>
)
const FilterRules = ({ filters }: { filters: FilterRuleModel[] }) => {
	const { t } = useTranslation()

	const dateFormat = useAppStore((state) => state.dateFormat)
	const { fieldConfig } = useFamilyRulesFieldConfig()

	return (
		<div className='inert grid gap-1.5' inert>
			<Label className='text-sm'>{t('app.filters')}</Label>
			<QueryBuilder
				dateFormat={dateFormat}
				rules={filters || []}
				fieldConfig={fieldConfig || {}}
				locale={localization}
				onRulesChange={() => {}}
			>
				<QueryBuilderRulesList className='pointer-events-none cursor-not-allowed border-y py-2 opacity-50' />
			</QueryBuilder>
		</div>
	)
}

const AggregationRules = ({ aggregation }: { aggregation: string[] }) => {
	const { t } = useTranslation()

	const { fields } = useFamilyRulesFieldConfig()

	return (
		<div className='space-y-2'>
			<Label className='mb-2 text-sm'>
				{t(
					'anchoring_management.strategy.tab.anchor_families.aggregation_column.title'
				)}
			</Label>
			{!!aggregation?.length &&
				aggregation?.map((rule) => {
					const ruleField = fields?.find((field) => field?.value === rule)
					const fieldType = getDefaultEditorByValueType(
						ruleField?.valueType as ValueType
					)

					return (
						<div
							key={rule}
							className='flex h-8 w-1/2 cursor-not-allowed items-center gap-2 rounded-lg border px-3 py-0.5 text-sm opacity-50'
						>
							<div className='flex-1 truncate'>
								<div className='flex items-center gap-2 truncate'>
									{getFieldIcon({
										name: fieldType,
									} as FieldType) || ''}
									<span className='truncate'>{ruleField?.label || rule}</span>
								</div>
							</div>
							<ChevronsUpDownIcon className='size-4 shrink-0 opacity-50' />
						</div>
					)
				})}
		</div>
	)
}
